import { useQuery } from '@tanstack/react-query';
import { RegisterScreen } from '@/components/RegisterScreen';
import { lineNumber, type FormLine, type FormValues } from '@/components/ChildRecords';
import { StatusPill, formatDate, formatNumber, todayIso } from '@/components/live';
import { ValidityBadge } from '@/components/ValidityBadge';
import { plotOptions } from '@/api/lookups';
import { usePickers } from '@/hooks/usePickers';
import { isSupabaseConfigured } from '@/lib/supabase';
import { optionLabel } from '@/lib/practiceLists';
import { HARVEST_CONDITIONS } from '@/lib/harvestConditions';
import { useRows } from '@/hooks/useResource';

interface Row {
  id: string; permit_code: string | null; farmer_id: string; farmer_name: string; farmer_code: string | null; plot_id: string | null;
  plot_code: string | null; permit_type: string; authority: string; authority_office: string | null; application_no: string | null;
  applied_on: string | null; permit_status: string; effective_status: string; permit_no: string | null; issued_on: string | null;
  valid_from: string | null; valid_to: string | null; permitted_lines: Array<Record<string, unknown>>; permitted_trees: number | null;
  trees_harvested: number; permit_class: string; conditions: string[]; government_permit_no: string | null;
}

export const PERMIT_TYPES = [
  { id: 'felling_and_transit', label: 'Felling permission and transit pass' },
  { id: 'felling', label: 'Felling permission' },
  { id: 'transit', label: 'Transit pass (TP)' },
  { id: 'exempt_registration', label: 'Exempt species - registration / intimation only' },
];
const AUTHORITIES = [
  { id: 'forest_department', label: 'Forest Department' }, { id: 'revenue_department', label: 'Revenue Department (Tahsildar)' },
  { id: 'panchayat', label: 'Gram Panchayat' }, { id: 'tree_authority', label: 'Tree Authority / Tree Officer' },
  { id: 'other', label: 'Other' },
];
// An internal permit is issued by the organisation itself; it is shown, never chosen.
const AUTHORITY_LABELS = [...AUTHORITIES, { id: 'organisation', label: 'The organisation (internal)' }];
export const PERMIT_STATUS = [
  { id: 'preparing', label: 'Preparing application' }, { id: 'applied', label: 'Applied' },
  { id: 'under_verification', label: 'Under field verification' }, { id: 'granted', label: 'Granted' },
  { id: 'refused', label: 'Refused' }, { id: 'withdrawn', label: 'Withdrawn' },
];

/**
 * Government harvest permits (owner, 2026-09-27: "Need to add option to facilitate government
 * harvest permits?").
 *
 * A harvest used to carry only a photo of its permit. Nothing followed the permit itself: when it
 * was applied for, where it stood, what it allowed, or whether a felling stayed inside it. Here each
 * permit is a record from application to grant, with the trees and volume permitted per species;
 * a harvest then names its permit, and the database refuses one outside the permit's dates, for a
 * species it does not cover, or past the trees it allows (migration 20260927490000).
 */
/** The Forest Department's permits (owner, 2026-09-27: "FD Permits"). */
export default function HarvestPermitsScreen() {
  return <PermitRegister kind="government" />;
}

/** The group's own approval to fell (owner, 2026-09-27: "Harvest Permits (Internal)"). */
export function InternalPermitsScreen() {
  return <PermitRegister kind="internal" />;
}

const INTERNAL_STATUS = [
  { id: 'preparing', label: 'Being prepared' }, { id: 'applied', label: 'Requested by the farmer' },
  { id: 'under_verification', label: 'Field check' }, { id: 'granted', label: 'Approved' },
  { id: 'refused', label: 'Refused' }, { id: 'withdrawn', label: 'Withdrawn' },
];

function PermitRegister({ kind }: { kind: 'internal' | 'government' }) {
  const internal = kind === 'internal';
  // An internal permit names the FD permit it goes with, when the species needs one.
  const fdPermits = useRows<{ id: string; farmer_id: string; permit_no: string | null; permit_code: string | null; valid_to: string | null }>(
    'harvest_permits', { filters: { permit_class: 'government' } }, internal);
  const pick = usePickers(['farmers', 'species', 'units']);
  const plots = useQuery({ queryKey: ['picker', 'plots', 'with-farmer'], queryFn: () => plotOptions(), enabled: isSupabaseConfigured });
  const plotsOf = (form: FormValues) => (plots.data ?? [])
    .filter((plot) => !form.farmer_id || plot.farmer_id === form.farmer_id)
    .map((plot) => ({ id: plot.id, label: plot.label }));
  const decided = (form: FormValues) => ['granted', 'refused'].includes(String(form.permit_status));
  const granted = (form: FormValues) => form.permit_status === 'granted';
  const lines = (form: FormValues) => (Array.isArray(form.permitted_lines) ? form.permitted_lines as FormLine[] : []);

  return (
    <RegisterScreen<Row>
      plotFilter
      filters={{ permit_class: kind }}
      fixedValues={internal ? { permit_class: 'internal', authority: 'organisation', permit_type: 'felling' } : { permit_class: 'government' }}
      addLabel={internal ? 'Add internal permit' : 'Add FD permit'} exportName={internal ? 'internal-harvest-permits' : 'fd-permits'} recordNoun="permit"
      orderBy={{ column: 'created_at', ascending: false }}
      recordLabel={(row) => `permit ${row.permit_no ?? row.permit_code ?? ''} - ${row.farmer_name}`}
      screenKey="plot.harvest"
      searchColumns={['permit_code', 'permit_no', 'application_no', 'farmer_name', 'farmer_code', 'plot_code']}
      secondary={{ sum: (row) => row.permitted_trees ?? 0, unit: 'trees', digits: 0 }}
      subtitle={internal
        ? 'The group\'s own approval to fell, before any tree comes down: what, how many, when, and on what conditions.'
        : 'Forest Department felling permissions and transit passes, from application to grant, and what has been harvested under each.'}
      table="ops.harvest_permits" title={internal ? 'Harvest permits (internal)' : 'FD permits'} view="harvest_permits"
      columns={[
        { key: 'permit_code', label: 'Ref', render: (row) => (
          <span className="cell-stack"><strong>{row.permit_no ?? row.permit_code ?? '--'}</strong>
            <small className="muted">{optionLabel(PERMIT_TYPES, row.permit_type)}</small></span>
        ) },
        { key: 'farmer_name', label: 'Farmer', render: (row) => (
          <span className="cell-stack"><strong>{row.farmer_name}</strong>
            <small className="muted">{[row.farmer_code, row.plot_code].filter(Boolean).join(' · ')}</small></span>
        ) },
        { key: 'authority', label: 'Authority', render: (row) => (
          <span className="cell-stack">{optionLabel(AUTHORITY_LABELS, row.authority)}<small className="muted">{row.authority_office ?? ''}</small></span>
        ) },
        { key: 'applied_on', label: 'Applied', render: (row) => formatDate(row.applied_on) },
        { key: 'effective_status', label: 'Status', render: (row) => <StatusPill value={row.effective_status} /> },
        { key: 'valid_to', label: 'Valid', render: (row) => (row.valid_to ? <ValidityBadge compact from={row.valid_from} to={row.valid_to} /> : '--') },
        ...(internal ? [{ key: 'government_permit_no', label: 'FD permit', render: (row: Row) => row.government_permit_no ?? <span className="muted">--</span> }] : []),
        { key: 'permitted_trees', label: 'Trees permitted / felled', align: 'right', render: (row) => (row.permitted_trees == null
          ? formatNumber(row.trees_harvested, 0) : `${formatNumber(row.permitted_trees, 0)} / ${formatNumber(row.trees_harvested, 0)}`) },
      ]}
      stats={[
        { key: 'pending', label: 'Awaiting decision', tone: 'info', match: (row) => ['preparing', 'applied', 'under_verification'].includes(row.permit_status) },
        { key: 'granted', label: 'Granted and valid', tone: 'ok', match: (row) => row.effective_status === 'granted' },
        { key: 'expiring', label: 'Expiring in 30 days', tone: 'warn', match: (row) => row.effective_status === 'granted' && Boolean(row.valid_to)
          && row.valid_to! <= new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10) },
        { key: 'expired', label: 'Expired', tone: 'error', match: (row) => row.effective_status === 'expired' },
        { key: 'refused', label: 'Refused', tone: 'error', match: (row) => row.permit_status === 'refused' },
      ]}
      defaults={{ permit_type: 'felling_and_transit', authority: 'forest_department', permit_status: 'preparing' }}
      fields={internal ? [
        { key: 'farmer_id', label: 'Farmer', type: 'select', options: pick.farmers, required: true },
        { key: 'plot_id', label: 'Plot', type: 'select', optionsFor: plotsOf, required: true,
          placeholderFor: (form) => (form.farmer_id ? 'Select the plot' : 'Choose the farmer first') },
        { key: 'government_permit_id', label: 'Forest Department permit', type: 'select',
          optionsFor: (form) => (fdPermits.data ?? []).filter((row) => !form.farmer_id || row.farmer_id === form.farmer_id)
            .map((row) => ({ id: row.id, label: `${row.permit_no ?? row.permit_code ?? ''}${row.valid_to ? `, valid to ${row.valid_to}` : ''}` })),
          placeholder: 'None needed, or not yet granted', hint: 'The FD permit this felling also needs, once it is on FD permits.' },
        { key: 'application_no', label: 'Request no.', type: 'text', section: 'Request', placeholder: 'e.g. the farmer\'s harvest request' },
        { key: 'applied_on', label: 'Requested on', type: 'date', max: todayIso() },
        { key: 'application_document_id', label: 'Request / tree marking sheet', type: 'file' },
        { key: 'permitted_lines', label: 'Trees requested / approved', type: 'lines', linesInColumn: true, addLineLabel: 'Add species',
          hint: 'Per species. A harvest under this permit cannot pass these trees.',
          columns: [
            { key: 'species_id', label: 'Species', type: 'select', options: pick.species, required: true, minWidth: 190 },
            { key: 'trees', label: 'Trees', type: 'number', total: true },
            { key: 'volume', label: 'Volume', type: 'number', total: true },
            { key: 'unit_id', label: 'Unit', type: 'select', options: pick.units, minWidth: 150 },
          ] },
        { key: 'conditions', label: 'Conditions of the permit', type: 'multi', toggles: true, options: HARVEST_CONDITIONS, section: 'Conditions',
          hint: 'The harvest report says which of these were met.' },
        { key: 'permit_status', label: 'Status', type: 'select', options: INTERNAL_STATUS, required: true, section: 'Decision' },
        { key: 'permit_no', label: 'Internal permit no.', type: 'text', required: granted, showIf: decided, placeholder: 'e.g. HP/2026/014' },
        { key: 'issued_on', label: 'Approved on', type: 'date', showIf: decided, max: todayIso() },
        { key: 'valid_from', label: 'Harvest window from', type: 'date', showIf: granted },
        { key: 'valid_to', label: 'Harvest window until', type: 'date', required: granted, showIf: granted },
        { key: 'permit_document_id', label: 'Signed permit', type: 'file', showIf: decided },
        { key: 'remarks', label: 'Remarks', type: 'textarea' },
      ] : [
        { key: 'farmer_id', label: 'Farmer', type: 'select', options: pick.farmers, required: true },
        { key: 'plot_id', label: 'Plot', type: 'select', optionsFor: plotsOf,
          placeholderFor: (form) => (form.farmer_id ? 'All of the farmer\'s plots' : 'Choose the farmer first'),
          hint: 'The plot the trees stand on. Blank when one permit covers several of the farmer\'s plots.' },
        { key: 'permit_type', label: 'Permit', type: 'select', options: PERMIT_TYPES, required: true,
          hint: 'Many states exempt planted species such as eucalyptus, silver oak or poplar: they need only a registration or intimation.' },
        { key: 'authority', label: 'Issuing authority', type: 'select', options: AUTHORITIES, required: true },
        { key: 'authority_office', label: 'Office', type: 'text', placeholder: 'e.g. Range Forest Office, Sirsi' },
        { key: 'application_no', label: 'Application no.', type: 'text', section: 'Application', placeholder: 'e.g. RFO/SRS/2026/0412' },
        { key: 'applied_on', label: 'Applied on', type: 'date', max: todayIso() },
        { key: 'fee_paid', label: 'Fee paid (₹)', type: 'number' },
        { key: 'application_document_id', label: 'Application copy', type: 'file' },
        { key: 'permitted_lines', label: 'Trees applied for / permitted', type: 'lines', linesInColumn: true, addLineLabel: 'Add species',
          hint: 'Per species: what was applied for, then what the permit allows once granted. A harvest cannot pass these trees.',
          columns: [
            { key: 'species_id', label: 'Species', type: 'select', options: pick.species, required: true, minWidth: 190 },
            { key: 'trees', label: 'Trees', type: 'number', total: true },
            { key: 'volume', label: 'Volume', type: 'number', total: true },
            { key: 'unit_id', label: 'Unit', type: 'select', options: pick.units, minWidth: 150 },
          ] },
        { key: 'permit_status', label: 'Status', type: 'select', options: PERMIT_STATUS, required: true, section: 'Decision' },
        { key: 'permit_no', label: 'Permit no.', type: 'text', required: granted, showIf: decided, placeholder: 'e.g. TP/KA/2026/118734' },
        { key: 'issued_on', label: 'Issued on', type: 'date', showIf: decided, max: todayIso() },
        { key: 'valid_from', label: 'Valid from', type: 'date', showIf: granted },
        { key: 'valid_to', label: 'Valid until', type: 'date', required: granted, showIf: granted },
        { key: 'permit_document_id', label: 'Permit / order', type: 'file', showIf: decided },
        { key: 'remarks', label: 'Remarks', type: 'textarea' },
      ]}
      validate={(form) => ({
        valid_to: form.valid_from && form.valid_to && String(form.valid_to) < String(form.valid_from)
          ? 'The permit cannot end before it starts.' : undefined,
        permitted_lines: lines(form).some((line) => line.species_id && lineNumber(line.trees) === null && lineNumber(line.volume) === null)
          ? 'Give the trees or the volume for every species.' : undefined,
      })}
    />
  );
}
